/** Date and number formatting for the interface. */

import { daysBetween, parseIso } from '../../shared/dates.js';
import type { IsoDate } from '../../shared/types.js';

/** Formats a day as a compact label such as `21 Sep`, suitable for a chart axis. */
export function formatShortDate(date: IsoDate): string {
  return parseIso(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Formats a day as a full label such as `Monday, 21 September 2026`. */
export function formatLongDate(date: IsoDate): string {
  return parseIso(date).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Formats a day as a medium label such as `Mon, 21 Sep`, used in tight headers. */
export function formatMediumDate(date: IsoDate): string {
  return parseIso(date).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Formats a session date for a narrow header, such as `Wed, 23 Sep`.
 *
 * The year is only shown when it differs from `reference`, because repeating the current year
 * in a header costs space that long weekday names need on a phone.
 */
export function formatSessionDate(date: IsoDate, reference: IsoDate): string {
  const sameYear = date.slice(0, 4) === reference.slice(0, 4);
  return parseIso(date).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Formats the month and year of a day, such as `September 2026`. */
export function formatMonth(date: IsoDate): string {
  return parseIso(date).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Formats a timestamp as a clock time such as `14:03`. */
export function formatClock(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Returns `singular` when `count` is one, otherwise `plural`. */
export function plural(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/** Days either side of today that get a word instead of a date. */
const NAMED_OFFSETS: Record<number, string> = {
  [-1]: 'Yesterday',
  0: 'Today',
  1: 'Tomorrow',
};

/**
 * Describes how far `date` is from `reference` in words.
 *
 * Returns `null` when the gap is large enough that a relative phrase stops being useful and
 * the formatted date already says everything.
 */
export function relativeDayLabel(date: IsoDate, reference: IsoDate): string | null {
  const offset = daysBetween(reference, date);
  const named = NAMED_OFFSETS[offset];
  if (named !== undefined) return named;
  if (Math.abs(offset) > 30) return null;
  const weeks = Math.round(Math.abs(offset) / 7);
  const magnitude = Math.abs(offset) < 7
    ? `${Math.abs(offset)} ${plural(Math.abs(offset), 'day', 'days')}`
    : `${weeks} ${plural(weeks, 'week', 'weeks')}`;
  return offset > 0 ? `In ${magnitude}` : `${magnitude} ago`;
}
